import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FilmSlate } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Signup() {
  const { signup, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) { nav("/"); return null; }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success("Welcome to Motographist Ops");
      nav("/");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain flex items-center justify-center p-6 relative">
      <div className="absolute top-6 left-8 flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-[#D97706] flex items-center justify-center">
          <FilmSlate size={18} weight="duotone" color="#0A0A0A" />
        </div>
        <div>
          <div className="text-[15px] font-extrabold tracking-tight leading-none">Motographist</div>
          <div className="text-[10px] text-white/40 mt-0.5 tracking-widest uppercase">Ops</div>
        </div>
      </div>

      <div className="w-full max-w-[400px] fade-up relative z-10">
        <div className="mb-8">
          <div className="text-xs text-[#D97706] uppercase tracking-widest mb-3">Create account</div>
          <h1 className="text-4xl font-extrabold tracking-tight leading-[1.05]">
            Start running <br /> your studio<br />
            <span className="text-white/40">the smart way.</span>
          </h1>
          <p className="text-sm text-white/50 mt-4 max-w-[340px]">
            Free to get started. Manage clients, projects, crew and finances end-to-end.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/40">Full name</label>
            <input
              data-testid="signup-name"
              type="text" required minLength={2}
              value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-white/30 focus:outline-none transition-colors"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/40">Email</label>
            <input
              data-testid="signup-email"
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-white/30 focus:outline-none transition-colors"
              placeholder="you@studio.com"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/40">Password</label>
            <input
              data-testid="signup-password"
              type="password" required minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-white/30 focus:outline-none transition-colors"
              placeholder="Minimum 6 characters"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/40">Confirm password</label>
            <input
              data-testid="signup-confirm"
              type="password" required minLength={6}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-white/30 focus:outline-none transition-colors"
              placeholder="Repeat password"
            />
          </div>
          <button
            data-testid="signup-submit"
            disabled={loading}
            className="w-full bg-white text-black font-semibold text-sm rounded-md py-2.5 hover:bg-white/90 transition-colors active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? "Creating account…" : "Create account →"}
          </button>
        </form>

        <div className="mt-6 text-xs text-white/50 text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="link-to-login" className="text-[#D97706] hover:text-[#F59E0B] font-medium">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
